import { delay, http, HttpResponse } from "msw";
import * as XLSX from "xlsx";

import {
  getGetNotificationHubEmailsMockHandler,
  getGetNotificationHubFilterOptionsMockHandler,
} from "@vega-api/generated/notification-hub-v1/notification-hub-v1.msw";
import { type ApiResponseEmailPreviewDetail } from "@vega-api/generated/types/apiResponseEmailPreviewDetail";
import { type ApiResponseNotificationHubEmails } from "@vega-api/generated/types/apiResponseNotificationHubEmails";
import { EmailStatus } from "@vega-api/generated/types/emailStatus";
import { type MailjetTemplateResponse } from "@vega-api/generated/types/mailjetTemplateResponse";
import { type NotificationHubEmailItem } from "@vega-api/generated/types/notificationHubEmailItem";
import {
  getGetEmailPreviewDetailMockHandler,
  getGetTemplateDetailContentMockHandler,
} from "@vega-api/generated/workbench-v1/workbench-v1.msw";

/**
 * Custom MSW handler for the Notification Hub email list.
 *
 * Orval's global mock config strips `meta` from generated mocks, but the hub's result
 * count and pagination both read `meta` — so we return a realistic, paginated response
 * (with meta.count + page math) that honours the requested page/pageSize. Lets the
 * overview page demonstrate the exact-count headline and server-side pagination under MSW.
 */
const TEMPLATE_TYPES: [string, string][] = [
  ["APOLLO_CAPITAL_CALL_NOTICE_TEMPLATE", "Capital call notice"],
  ["APOLLO_SUBSCRIPTION_DOCS_READY_TEMPLATE", "Subscription documents ready"],
  ["APOLLO_WELCOME_EMAIL_EMPLOYEE_TEMPLATE", "Welcome email"],
  ["APOLLO_QUARTERLY_STATEMENT_TEMPLATE", "Quarterly statement"],
  ["APOLLO_DISTRIBUTION_NOTICE_TEMPLATE", "Distribution notice"],
  ["APOLLO_TAX_DOC_REQUEST_TEMPLATE", "Tax document request"],
];

const SENDERS = [
  "no-reply@mail.apollo.vega.investments",
  "notifications@mail.apollo.vega.investments",
];

const RECIPIENTS = [
  "ops@mapletrust.example",
  "ir@cedarcapital.example",
  "admin@brightwater.example",
  "contact@summitlp.example",
  "finance@harborview.example",
];

const RECIPIENT_NAMES: Record<string, string> = {
  "ops@mapletrust.example": "Maple Trust Operations",
  "ir@cedarcapital.example": "Cedar Capital IR",
  "admin@brightwater.example": "Brightwater Admin",
  "contact@summitlp.example": "Summit LP",
  "finance@harborview.example": "Harborview Finance",
};

// Recipient → investor id, used to drive the drawer's "View user page" link.
// `finance@harborview.example` is intentionally omitted to simulate a system
// email not tied to a platform user (link should be hidden for those rows).
const RECIPIENT_USER_IDS: Record<string, string> = {
  "ops@mapletrust.example": "11111111-1111-4111-8111-111111111111",
  "ir@cedarcapital.example": "22222222-2222-4222-8222-222222222222",
  "admin@brightwater.example": "33333333-3333-4333-8333-333333333333",
  "contact@summitlp.example": "44444444-4444-4444-8444-444444444444",
};

const STATUSES: EmailStatus[] = [
  EmailStatus.OPENED,
  EmailStatus.SENT,
  EmailStatus.CLICKED,
  EmailStatus.BOUNCE,
  EmailStatus.BLOCKED,
  EmailStatus.QUEUED,
  EmailStatus.PROCESSED,
];

// Welcome / activation templates whose body the preview endpoint withholds (variables left
// unsubstituted) — mirrors the server's CommunicationTopicName.isPreviewRestricted set so the
// drawer shows the "preview restricted" notice for these rows under MSW.
const PREVIEW_RESTRICTED_TEMPLATES = new Set<string>([
  "APOLLO_WELCOME_EMAIL_EMPLOYEE_TEMPLATE",
]);

const TOTAL = 412;
const BASE_MS = new Date("2026-06-10T12:00:00Z").getTime();
const ID_OFFSET = 100000000000;

const makeRow = (i: number): NotificationHubEmailItem => {
  const [templateName, templateDescription] =
    TEMPLATE_TYPES[i % TEMPLATE_TYPES.length];
  const recipient = RECIPIENTS[i % RECIPIENTS.length];

  return {
    communicationLedgerId: `0a1b2c3d-0000-4000-8000-${String(ID_OFFSET + i)}`,
    timestamp: new Date(BASE_MS - i * 3.5 * 3600 * 1000).toISOString(),
    templateName,
    templateDescription,
    recipient,
    userId: RECIPIENT_USER_IDS[recipient],
    sender: SENDERS[i % SENDERS.length],
    recipientEmailStatus: STATUSES[i % STATUSES.length],
  };
};

const buildEmailsResponse = (info: {
  request: Request;
}): ApiResponseNotificationHubEmails => {
  const url = new URL(info.request.url);
  const page = Math.max(1, Number(url.searchParams.get("page") ?? "1"));
  const pageSize = Math.max(
    1,
    Number(url.searchParams.get("pageSize") ?? "50"),
  );

  const count = TOTAL;
  const totalPages = Math.ceil(count / pageSize);
  const start = (page - 1) * pageSize;

  const data: NotificationHubEmailItem[] = [];
  for (let i = start; i < Math.min(start + pageSize, count); i++) {
    data.push(makeRow(i));
  }

  return {
    data,
    meta: { count, page, pageSize, totalPages, hasNextPage: page < totalPages },
    errors: null,
  };
};

const NOTIFICATION_HUB_EXPORT_URL =
  "*/api/communications/v1/workbench/notification-hub/export";

const EXPORT_COLUMNS = [
  "Date / Time (UTC)",
  "Type",
  "Email Address",
  "Sender",
  "Status",
];

const parseTimeParam = (value: string | null): number | undefined => {
  if (value === null || value === "") {
    return undefined;
  }

  const ms = Date.parse(value);

  return Number.isNaN(ms) ? undefined : ms;
};

const formatExportTimestamp = (iso: string): string =>
  `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;

/**
 * Builds the export workbook from the same mock dataset as the list, applying the
 * query-param filters so the downloaded file reflects the active filters (PRD §5.4).
 * Columns mirror the list view: sent at (UTC), type, recipient email, sender, status.
 * The real workbook is generated and richly styled server-side; this mock proves the
 * filter-aware, formatted .xlsx download end to end (header row, sized columns,
 * auto-filter).
 */
const buildExportWorkbook = (info: { request: Request }): ArrayBuffer => {
  const url = new URL(info.request.url);
  const templateNames = url.searchParams.getAll("templateName");
  const statuses = url.searchParams.getAll("recipientStatus");
  const senders = url.searchParams.getAll("sender");
  const search = (url.searchParams.get("searchQuery") ?? "").toLowerCase();
  const startMs = parseTimeParam(url.searchParams.get("startTime"));
  const endMs = parseTimeParam(url.searchParams.get("endTime"));

  const rows: string[][] = [EXPORT_COLUMNS];

  for (let i = 0; i < TOTAL; i++) {
    const row = makeRow(i);
    const rowMs = Date.parse(row.timestamp);

    if (templateNames.length > 0 && !templateNames.includes(row.templateName)) {
      continue;
    }

    if (statuses.length > 0 && !statuses.includes(row.recipientEmailStatus)) {
      continue;
    }

    if (senders.length > 0 && !senders.includes(row.sender)) {
      continue;
    }

    if (search && !row.recipient.toLowerCase().includes(search)) {
      continue;
    }

    if (startMs !== undefined && rowMs < startMs) {
      continue;
    }

    if (endMs !== undefined && rowMs > endMs) {
      continue;
    }

    rows.push([
      formatExportTimestamp(row.timestamp),
      row.templateDescription,
      row.recipient,
      row.sender,
      row.recipientEmailStatus,
    ]);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet["!cols"] = [
    { wch: 20 },
    { wch: 28 },
    { wch: 30 },
    { wch: 40 },
    { wch: 12 },
  ];
  worksheet["!autofilter"] = { ref: `A1:E${rows.length}` };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sent emails");

  return XLSX.write(workbook, {
    type: "array",
    bookType: "xlsx",
  }) as ArrayBuffer;
};

/**
 * On-demand single-email preview (PRD §5.2). Reconstructs the row the user clicked from the
 * `communicationLedgerId` so the drawer's metadata (recipient / sender / status / type) matches
 * the list, and returns server-rendered-style HTML. Welcome/activation rows come back
 * `previewRestricted` with the body variables left intact — so the drawer renders the security
 * notice instead of the body, exactly as the real endpoint behaves.
 */
const buildEmailPreviewDetail = (info: {
  request: Request;
}): ApiResponseEmailPreviewDetail => {
  const communicationLedgerId =
    new URL(info.request.url).pathname.split("/").pop() ?? "";
  const parsedIndex =
    Number(communicationLedgerId.split("-").pop()) - ID_OFFSET;
  const index =
    Number.isFinite(parsedIndex) && parsedIndex >= 0 && parsedIndex < TOTAL
      ? parsedIndex
      : 0;
  const row = makeRow(index);
  const previewRestricted = PREVIEW_RESTRICTED_TEMPLATES.has(row.templateName);
  const recipientName = RECIPIENT_NAMES[row.recipient] ?? "Investor";

  return {
    data: {
      communicationLedgerId,
      html: buildPreviewHtml(row, recipientName, previewRestricted),
      subject: row.templateDescription,
      senderName: "Investor Relations",
      timestamp: row.timestamp,
      sender: row.sender,
      recipient: row.recipient,
      recipientName,
      recipientEmailStatus: row.recipientEmailStatus,
      previewRestricted,
    },
    errors: null,
  };
};

const buildPreviewHtml = (
  row: NotificationHubEmailItem,
  recipientName: string,
  restricted: boolean,
): string => {
  // Restricted bodies keep their {{var:...}} placeholders (the real endpoint never substitutes
  // them); the drawer shows the security notice for these, not the body.
  const greeting = restricted
    ? "Dear {{var:firstName}},"
    : `Dear ${recipientName},`;
  const lead = restricted
    ? "Your account for {{var:fundName}} is ready. Activate it using the secure link below."
    : `This is a copy of the "${row.templateDescription}" notification.`;
  const cta = restricted
    ? "{{var:actionLink}}"
    : "https://app.vega.investments";

  return `
    <div style="font-family: Arial, sans-serif; color: #1a1a1a; max-width: 600px; margin: 0 auto; border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden;">
      <div style="background-color: #1f5f3f; padding: 20px 24px;">
        <span style="color: #ffffff; letter-spacing: 2px; font-weight: 700; font-size: 14px;">INVESTOR PORTAL</span>
      </div>
      <div style="padding: 24px;">
        <p style="margin: 0 0 16px;">${greeting}</p>
        <h1 style="font-size: 20px; margin: 0 0 12px;">${row.templateDescription}</h1>
        <p style="margin: 0 0 16px; line-height: 1.5;">${lead}</p>
        <a href="${cta}" style="display: inline-block; background-color: #1f5f3f; color: #ffffff; padding: 10px 20px; border-radius: 6px; text-decoration: none; font-weight: 600;">Open the investor portal</a>
        <p style="margin: 24px 0 0; font-size: 13px; color: #6a6a6a;">This message was sent to ${row.recipient}.</p>
      </div>
      <div style="padding: 16px 24px; border-top: 1px solid #e5e5e5; font-size: 12px; color: #8a8a8a;">
        You are receiving this email because you have an account on the investor portal.
      </div>
    </div>
  `;
};

/**
 * Realistic template-detail content for the Template library preview. The generated stub
 * returns `htmlPart: "string"`, which makes the preview unreviewable, so we return branded
 * Mailjet-style HTML with `{{var:...}}` placeholders intact (the preview highlights, never
 * fills, them). The greeting differs per version so switching Default ↔ Alternative is
 * visibly distinct, and the CTA link is a placeholder href (the preview disables it).
 */
const TEMPLATE_BODY: Record<
  string,
  { title: string; lead: string; cta: string }
> = {
  APOLLO_CAPITAL_CALL_NOTICE_TEMPLATE: {
    title: "Capital call notice",
    lead: "A new capital call of {{var:callAmount}} has been issued for {{var:fundName}}. Payment is due by {{var:dueDate}}.",
    cta: "View capital call",
  },
  APOLLO_SUBSCRIPTION_DOCS_READY_TEMPLATE: {
    title: "Your subscription documents are ready",
    lead: "Your subscription documents for {{var:fundName}} are ready to review and sign.",
    cta: "Review documents",
  },
};

const FALLBACK_BODY = {
  title: "Welcome to the Apollo investor portal",
  lead: "Your account for {{var:fundName}} has been created and is ready to activate.",
  cta: "Activate account",
};

const buildTemplateDetailHtml = (
  templateName: string,
  templateType: string,
): string => {
  const body = TEMPLATE_BODY[templateName] ?? FALLBACK_BODY;
  const isAlternative = templateType === "INSTITUTIONAL";
  const greeting = isAlternative
    ? "Dear {{var:institutionName}} team,"
    : "Dear {{var:firstName}},";
  const audienceNote = isAlternative
    ? "Alternative (institutional) version"
    : "Default version";

  return `
    <div style="font-family: Arial, sans-serif; color: #1a1a1a; max-width: 600px; margin: 0 auto; border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden;">
      <div style="background-color: #1f5f3f; padding: 20px 24px;">
        <span style="color: #ffffff; letter-spacing: 3px; font-weight: 700; font-size: 16px;">APOLLO</span>
      </div>
      <div style="padding: 24px;">
        <p style="font-size: 12px; color: #8a8a8a; margin: 0 0 16px;">${audienceNote}</p>
        <p style="margin: 0 0 16px;">${greeting}</p>
        <h1 style="font-size: 20px; margin: 0 0 12px;">${body.title}</h1>
        <p style="margin: 0 0 16px; line-height: 1.5;">${body.lead}</p>
        <p style="margin: 0 0 24px; line-height: 1.5;">If you have any questions, contact your relationship manager at {{var:supportEmail}}.</p>
        <a href="{{var:actionLink}}" style="display: inline-block; background-color: #1f5f3f; color: #ffffff; padding: 10px 20px; border-radius: 6px; text-decoration: none; font-weight: 600;">${body.cta}</a>
        <p style="margin: 24px 0 0; font-size: 13px; color: #6a6a6a;">If the button does not work, copy this link into your browser: {{var:actionLink}}</p>
      </div>
      <div style="padding: 16px 24px; border-top: 1px solid #e5e5e5; font-size: 12px; color: #8a8a8a;">
        You are receiving this email because you have an account on the Apollo investor portal. © Apollo Global Management {{var:year}}.
      </div>
    </div>
  `;
};

const buildTemplateDetail = (info: {
  request: Request;
}): MailjetTemplateResponse => {
  const url = new URL(info.request.url);
  const templateName = url.searchParams.get("templateName") ?? "";
  const templateType = url.searchParams.get("templateType") ?? "EMPLOYEE";
  const body = TEMPLATE_BODY[templateName] ?? FALLBACK_BODY;

  return {
    subject: body.title,
    htmlPart: buildTemplateDetailHtml(templateName, templateType),
  };
};

export const notificationHubHandlers = [
  getGetNotificationHubEmailsMockHandler((info) => buildEmailsResponse(info)),
  // Excel (.xlsx) export of the rows matching the current filters (PRD §5.4). The body is
  // the workbook bytes; the client reads them via responseType: "blob" and names the file.
  // We mirror the generated binary-download mocks (application/json content-type + delay)
  // so the mocked binary is delivered cleanly over MSW for the cross-origin API base — the
  // real endpoint returns the spreadsheetml content type per the contract.
  http.get(NOTIFICATION_HUB_EXPORT_URL, async (info) => {
    await delay(10);

    return new HttpResponse(buildExportWorkbook(info), {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }),
  // Dropdown values come straight from the contract example (templates / statuses / senders).
  getGetNotificationHubFilterOptionsMockHandler(),
  // On-demand email preview for the detail sidebar (overrides the generated stub).
  getGetEmailPreviewDetailMockHandler((info) => buildEmailPreviewDetail(info)),
  // Realistic template-detail HTML for the Template library preview (overrides the "string" stub).
  getGetTemplateDetailContentMockHandler((info) => buildTemplateDetail(info)),
];
